import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Banner from '@components/Banner';
import { handleExamsBannerLogic } from '@components/Banner/helpers';
import {
  useExamV2Access,
  useLearnosityExamAccess,
} from '@components/Component/LearnosityExams/ShowIfLearnosityExamFlagEnabled/ShowIfLearnosityExamFlagEnabled';

import { useUser } from '@reducers/user/hooks';
import {
  useGetLearnosityExamTestSessions,
  useGetTestSessionCompletionStatus,
} from '../LearnosityExams/hooks';

interface ExamBannerProps {
  onShowBannerChange?: (show: boolean) => void;
}

const SHOULD_SHOW_BANNER_DOMAIN_LIST = ['dashboard', 'lessons', 'schedule'];
const ExamBanner: React.FC<ExamBannerProps> = ({ onShowBannerChange }) => {
  const user = useUser();
  const params = useParams();
  const { data: testSessionCompletionStatus } = useGetTestSessionCompletionStatus(
    params.sectionId,
  );
  // Redux selectors
  const { data: testSessions, isLoading: isLoadingTestSessions } =
    useGetLearnosityExamTestSessions();
  const hasLearnosityExamAccess = useLearnosityExamAccess();
  const hasExamV2Access = useExamV2Access({ suspense: false });
  const bannerData = useMemo(() => {
    if (!hasExamV2Access && !hasLearnosityExamAccess) {
      return null;
    }
    if (
      !testSessions ||
      isLoadingTestSessions ||
      !testSessionCompletionStatus ||
      !hasLearnosityExamAccess
    ) {
      return null;
    }
    return handleExamsBannerLogic({
      exams: testSessions,
      user,
      testSessionCompletionStatus,
      isLearnosityExam: hasLearnosityExamAccess,
      params,
    });
  }, [
    testSessions,
    user,
    testSessionCompletionStatus,
    hasLearnosityExamAccess,
    params.courseId,
    params.sectionId,
    hasExamV2Access,
    isLoadingTestSessions,
  ]);
  const shouldShowBanner =
    bannerData && SHOULD_SHOW_BANNER_DOMAIN_LIST.includes(params.domain);
  useEffect(() => {
    onShowBannerChange?.(!!shouldShowBanner);
  }, [shouldShowBanner, onShowBannerChange]);

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <Banner
      bannerText={bannerData.bannerText}
      bannerAction="Go To Tests Page"
      bannerUrl={bannerData.bannerUrl}
      hideButton={false}
      bannerType={bannerData.bannerType}
    />
  );
};

export default ExamBanner;